import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarRange, PencilLine, RotateCcw, Send, History } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEur = (v) => (v == null ? "—" : new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(v));
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("de-AT") : "—");

const STATUS = {
  offen: ["Offen", { borderColor: "#CBD5E1", color: "#475569", background: "#F8FAFC" }],
  entwurf: ["Entwurf", { borderColor: "#C5A059", color: "#92650B", background: "#FFFBEB" }],
  versendet: ["Versendet", { borderColor: "#0A1128", color: "#0A1128", background: "#EEF2FF" }],
  gezahlt: ["Gezahlt", { borderColor: "#16A34A", color: "#166534", background: "#F0FDF4" }],
  extern: ["Extern erledigt", { borderColor: "#CBD5E1", color: "#64748B", background: "#F1F5F9" }],
};

const ACTION = { override: "Werte geändert", draft: "Als Entwurf gespeichert", reset: "Auf Automatik zurückgesetzt", approve: "Freigegeben & versendet" };
const FIELDS = [["basis_netto", "Einnahmen"], ["ust", "USt"], ["vstp", "Vorsteuer"], ["zahllast", "Zahllast"]];
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("de-AT", { dateStyle: "short", timeStyle: "short" }) : "—");

const HistoryRow = ({ row }) => (
  <tr className="bg-slate-50" data-testid={`lex-uva-year-history-${row.period}`}>
    <td colSpan={7} className="p-4">
      {!row.history?.length ? (
        <p className="text-xs text-slate-400" data-testid={`lex-uva-year-history-empty-${row.period}`}>Noch keine Änderungen – Werte vollautomatisch.</p>
      ) : (
        <ul className="space-y-3">
          {row.history.map((h) => (
            <li key={h.id} className="text-xs border-l-2 pl-3" style={{ borderColor: "#C5A059" }} data-testid={`lex-uva-history-entry-${h.id}`}>
              <div className="flex flex-wrap gap-x-3 text-slate-600">
                <span className="lex-mono">{fmtDT(h.at)}</span>
                <span className="font-semibold text-slate-800">{h.by || "—"}</span>
                <span>{ACTION[h.action] || h.action}</span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 lex-mono text-slate-500">
                {FIELDS.filter(([k]) => h.new && (h.old == null || h.old[k] !== h.new[k])).map(([k, label]) => (
                  <span key={k}>{label}: {h.old ? <><s>{fmtEur(h.old[k])}</s> → </> : null}<span className="text-slate-800">{fmtEur(h.new[k])}</span></span>
                ))}
              </div>
              {h.note && <p className="mt-1 text-slate-500">Grund: „{h.note}“</p>}
            </li>
          ))}
        </ul>
      )}
    </td>
  </tr>
);

// Netto geändert → USt (20 %) und Vorsteuerpauschale (1,8 %) neu berechnen; USt/Vorsteuer ändern nur sich selbst.
export const applyUvaField = (form, key, value) => {
  if (key !== "basis_netto") return { ...form, [key]: value };
  const net = parseFloat(String(value ?? "").replace(",", "."));
  if (!Number.isFinite(net)) return { ...form, basis_netto: value };
  const r = (x) => Math.round(x * 100) / 100;
  return { ...form, basis_netto: value, ust: r(net * 0.2), vstp: r(net * 0.018) };
};

const EditRow = ({ row, clientId, onSaved, onCancel }) => {
  const [form, setForm] = useState({ basis_netto: row.basis_netto ?? "", ust: row.ust ?? "", vstp: row.vstp ?? "", note: "" });
  const [busy, setBusy] = useState(false);
  const n = (v) => { const x = parseFloat(String(v ?? "").replace(",", ".")); return Number.isFinite(x) ? x : null; };
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/lex/advisor/uva/${clientId}/${row.period}/draft`, {
        basis_netto: n(form.basis_netto), ust: n(form.ust), vstp: n(form.vstp), note: form.note || null,
      });
      toast.success(`UVA ${row.period_label} als Entwurf gespeichert.`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setBusy(false); }
  };
  const field = (key, label) => (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
      <input type="number" step="0.01" value={form[key]} onChange={(e) => setForm(applyUvaField(form, key, e.target.value))}
        className="w-full border border-slate-200 px-2 py-1.5 text-sm lex-mono focus:outline-none focus:border-[#C5A059]"
        data-testid={`lex-uva-year-edit-${key}`} />
      {row.auto && <span className="text-[10px] text-slate-400 lex-mono">Automatik: {fmtEur(row.auto[key])}</span>}
    </label>
  );
  const zl = (n(form.ust) || 0) - (n(form.vstp) || 0);
  return (
    <tr className="bg-[#FDFCFB]" data-testid={`lex-uva-year-edit-row-${row.period}`}>
      <td colSpan={7} className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {field("basis_netto", "Einnahmen netto")}
          {field("ust", "Umsatzsteuer")}
          {field("vstp", "Vorsteuer (Ausgaben)")}
        </div>
        <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} maxLength={300}
          placeholder="Grund der Änderung (optional, intern)"
          className="w-full mt-3 border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:border-[#C5A059]"
          data-testid="lex-uva-year-edit-note" />
        <div className="flex items-center justify-between mt-3">
          <span className="text-sm text-slate-600">Zahllast: <span className="lex-mono font-semibold" data-testid="lex-uva-year-edit-zahllast">{fmtEur(zl)}</span></span>
          <div className="flex gap-2">
            <button type="button" onClick={onCancel} className="text-xs uppercase tracking-wider text-slate-500 px-3 py-1.5"
              data-testid="lex-uva-year-edit-cancel">Abbrechen</button>
            <button type="button" onClick={save} disabled={busy} className="lex-btn !px-4 !py-1.5 text-xs disabled:opacity-50"
              data-testid="lex-uva-year-edit-save">{busy ? "Speichert …" : "Als Entwurf speichern"}</button>
          </div>
        </div>
      </td>
    </tr>
  );
};

// Kanzlei-Portal: alle UVAs eines Mandanten pro Jahr — Status offen → entwurf → versendet → gezahlt.
export const LexUvaYearPanel = ({ clientId, onOpenPeriod, refreshKey = 0 }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showHist, setShowHist] = useState(null);

  const load = useCallback(() => {
    setData(null);
    axios.get(`${API}/lex/advisor/clients/${clientId}/uva-year`, { params: { year } })
      .then((res) => setData(res.data))
      .catch(() => setData({ periods: [], error: true }));
  }, [clientId, year]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const reset = async (row) => {
    try {
      await axios.delete(`${API}/lex/advisor/uva/${clientId}/${row.period}/draft`);
      toast.success(`UVA ${row.period_label} auf Automatik zurückgesetzt.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Zurücksetzen fehlgeschlagen.");
    }
  };

  const thisYear = new Date().getFullYear();
  const years = [thisYear + 1, thisYear, thisYear - 1, thisYear - 2];

  return (
    <div className="lex-card p-6" data-testid="lex-uva-year-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <CalendarRange className="h-4 w-4" style={{ color: "#C5A059" }} />
          <h2 className="text-xs uppercase tracking-widest font-semibold text-slate-500">UVA-Übersicht</h2>
          {data?.frequency && <span className="text-[11px] text-slate-400">· {data.frequency === "monthly" ? "monatlich" : "quartalsweise"}</span>}
        </div>
        <select value={year} onChange={(e) => { setEditing(null); setYear(Number(e.target.value)); }}
          className="border border-slate-200 px-3 py-1.5 text-xs bg-white lex-mono" data-testid="lex-uva-year-select">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {!data ? (
        <p className="text-sm text-slate-400" data-testid="lex-uva-year-loading">Lädt …</p>
      ) : data.error ? (
        <p className="text-sm text-slate-400" data-testid="lex-uva-year-error">Übersicht konnte nicht geladen werden.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="lex-uva-year-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-3 font-semibold">Periode</th>
                <th className="py-2 pr-3 font-semibold text-right">Einnahmen</th>
                <th className="py-2 pr-3 font-semibold text-right">USt</th>
                <th className="py-2 pr-3 font-semibold text-right">Vorsteuer</th>
                <th className="py-2 pr-3 font-semibold text-right">Zahllast</th>
                <th className="py-2 pr-3 font-semibold">Status</th>
                <th className="py-2 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {data.periods.map((r) => {
                const [label, style] = STATUS[r.status] || STATUS.offen;
                return [
                  <tr key={r.period} className="border-b border-slate-50" data-testid={`lex-uva-year-row-${r.period}`}>
                    <td className="py-2.5 pr-3">
                      <span className="lex-mono text-slate-800">{r.period_label}</span>
                      <span className="block text-[10px] text-slate-400">Frist {fmtDate(r.due_date)}</span>
                    </td>
                    <td className="py-2.5 pr-3 lex-mono text-right text-slate-700">{fmtEur(r.basis_netto)}</td>
                    <td className="py-2.5 pr-3 lex-mono text-right text-slate-700">{fmtEur(r.ust)}</td>
                    <td className="py-2.5 pr-3 lex-mono text-right text-slate-700">{fmtEur(r.vstp)}</td>
                    <td className="py-2.5 pr-3 lex-mono text-right font-semibold text-slate-900" data-testid={`lex-uva-year-zahllast-${r.period}`}>
                      {fmtEur(r.zahllast)}
                      {r.override && <span className="block text-[10px] font-normal" style={{ color: "#92650B" }}>manuell</span>}
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className="lex-mono text-[11px] px-2 py-0.5 border" style={style} data-testid={`lex-uva-year-status-${r.period}`}>
                        {r.future ? "Noch nicht fällig" : label}
                      </span>
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <div className="inline-flex gap-1.5">
                        {!r.future && (
                          <button type="button" title="Änderungsprotokoll" onClick={() => setShowHist(showHist === r.period ? null : r.period)}
                            className="relative p-1.5 border border-slate-200 text-slate-500 hover:text-[#0A1128] hover:border-[#C5A059] transition-colors"
                            data-testid={`lex-uva-year-history-btn-${r.period}`}>
                            <History className="h-3.5 w-3.5" />
                            {r.history?.length > 0 && (
                              <span className="absolute -top-1.5 -right-1.5 text-[9px] lex-mono px-1" style={{ background: "#0A1128", color: "#C5A059" }}>{r.history.length}</span>
                            )}
                          </button>
                        )}
                      {r.editable && (
                        <>
                          <button type="button" title="Werte bearbeiten" onClick={() => setEditing(editing === r.period ? null : r.period)}
                            className="p-1.5 border border-slate-200 text-slate-500 hover:text-[#0A1128] hover:border-[#C5A059] transition-colors"
                            data-testid={`lex-uva-year-edit-${r.period}`}><PencilLine className="h-3.5 w-3.5" /></button>
                          {r.status === "entwurf" && (
                            <button type="button" title="Auf Automatik zurücksetzen" onClick={() => reset(r)}
                              className="p-1.5 border border-slate-200 text-slate-500 hover:text-[#0A1128] hover:border-[#C5A059] transition-colors"
                              data-testid={`lex-uva-year-reset-${r.period}`}><RotateCcw className="h-3.5 w-3.5" /></button>
                          )}
                          <button type="button" title="Prüfen & an Mandanten versenden" onClick={() => onOpenPeriod(r.period)}
                            className="p-1.5 border border-slate-200 text-slate-500 hover:text-[#0A1128] hover:border-[#C5A059] transition-colors"
                            data-testid={`lex-uva-year-open-${r.period}`}><Send className="h-3.5 w-3.5" /></button>
                        </>
                      )}
                      </div>
                    </td>
                  </tr>,
                  showHist === r.period && <HistoryRow key={`${r.period}-hist`} row={r} />,
                  editing === r.period && (
                    <EditRow key={`${r.period}-edit`} row={r} clientId={clientId}
                      onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-400 mt-3">
        Standard: vollautomatisch berechnet. Werte sind bis einschließlich Entwurf änderbar. Die Zahlungsanweisung geht an den Mandanten, sobald Sie die UVA versenden.
      </p>
    </div>
  );
};

export default LexUvaYearPanel;
